import Api from '../services/Api';
import { ErrorDictionary } from '../pages/Components/Helpers/ErrorDictionary';
import { Response } from './Types';

/**
 * Devolução de venda.
 *
 * A ORDEM DAS CHAMADAS:
 *   Permitida  -> o pedido pode ser devolvido? Quando não pode, POR QUE não
 *   Itens      -> o que dá para devolver, e quanto ainda resta de cada LINHA
 *   Simular    -> a prévia, enquanto o operador escolhe as quantidades
 *   Gravar     -> o rascunho
 *   Boletos    -> o que será baixado no banco, para avisar ANTES
 *   Confirmar  -> baixa no banco e efetiva tudo
 *
 * TODO catch preserva o `ErroDetalhado` do corpo. As recusas da devolução ("este pedido já
 * tem parcela paga", "há boleto ainda ativo no banco") são escritas em português pelas
 * procedures, para o operador ler. Trocá-las pelo texto genérico do ErrorDictionary aqui
 * apagaria justamente a informação que resolve o problema dele — foi o defeito que o
 * ajuste de 11/08 corrigiu no cancelamento de boleto.
 */
class DevolucaoController {
  private static erro(error: any): Response {
    const corpo = error?.response?.data;

    return {
      StatusCode: error?.response?.status,
      Data: ErrorDictionary(error?.response?.status),
      ErroDetalhado:
        corpo?.ErroDetalhado ||
        corpo?.Erro ||
        (typeof corpo === 'string' ? corpo : undefined),
    };
  }

  /** Se o pedido pode ser devolvido, e o motivo quando não pode. */
  public static async Permitida(vendaId: number): Promise<any> {
    try {
      const response = await Api.get(`api/Devolucao/Permitida/${vendaId}`);
      return response?.data ?? null;
    } catch (error: any) {
      return null;
    }
  }

  /** Itens do pedido com o saldo devolvível de cada LINHA. */
  public static async Itens(vendaId: number, devolucaoId?: number | null): Promise<Response> {
    try {
      const query = devolucaoId ? `?devolucaoId=${devolucaoId}` : '';
      const response = await Api.get(`api/Devolucao/Itens/${vendaId}${query}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  /** Prévia. Não grava nada. Devolve { Resumo, Itens }. */
  public static async Simular(
    vendaId: number,
    itens: any[],
    condicaoPagamentoId?: number | null,
    dataReferencia?: string | null
  ): Promise<Response> {
    try {
      const response = await Api.post('api/Devolucao/Simular', {
        VendaId: vendaId,
        Itens: itens,
        CondicaoPagamentoId: condicaoPagamentoId ?? null,
        DataReferencia: dataReferencia || null,
      });
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  public static async Gravar(payload: {
    VendaId: number;
    DevolucaoId?: number | null;
    Motivo: string;
    Itens: any[];
    TipoPagamentoId: number;
    CondicaoPagamentoId: number;
    /** yyyy-MM-dd. É dela que saem os vencimentos das parcelas do saldo. */
    DataReferencia?: string | null;
  }): Promise<Response> {
    try {
      const response = await Api.post('api/Devolucao/Gravar', payload);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  /** Boletos que serão baixados no banco. Baixa de boleto não tem volta: avisar antes. */
  public static async Boletos(devolucaoId: number): Promise<Response> {
    try {
      const response = await Api.get(`api/Devolucao/Boletos/${devolucaoId}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  /**
   * Confirma a devolução.
   *
   * `destinoSaldo` só importa quando sobra valor A FAVOR do cliente - ele já pagou mais
   * do que a venda passou a valer depois da devolução. 'CREDITO' lança na conta corrente
   * dele; 'PAGAR' gera um contas a pagar, sempre à vista.
   *
   * Omitir é seguro: a procedure trata ausência como CREDITO, que é o destino que não
   * movimenta dinheiro. É o que permite a API subir antes desta tela.
   */
  public static async Confirmar(
    devolucaoId: number,
    destinoSaldo?: 'CREDITO' | 'PAGAR'
  ): Promise<Response> {
    try {
      const response = await Api.post(`api/Devolucao/Confirmar/${devolucaoId}`,
                                      destinoSaldo ? { DestinoSaldo: destinoSaldo } : {});
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  /**
   * Gera a nota fiscal da devolução: finalidade 4, nota de entrada, referenciando a chave
   * da NF-e da venda de origem.
   *
   * Só gera o registro — a transmissão continua sendo a da tela de notas fiscais.
   */
  /**
   * As notas fiscais de devolução TRANSMITIDAS de uma venda.
   *
   * Alimenta o grid da guia Financeiro. Lista vazia é resposta legítima — a maioria das
   * vendas nunca foi devolvida —, por isso o erro também devolve [] em vez de propagar:
   * um grid vazio é melhor que uma guia quebrada.
   */
  public static async NotasDaVenda(vendaId: number): Promise<Response> {
    try {
      const response = await Api.get(`api/Devolucao/NotasDaVenda/${vendaId}`);
      return { StatusCode: response.status, Data: response?.data ?? [] };
    } catch (error: any) {
      return { StatusCode: error?.response?.status, Data: [] };
    }
  }

  public static async GerarNota(devolucaoId: number): Promise<Response> {
    try {
      const response = await Api.post(`api/Devolucao/GerarNota/${devolucaoId}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }

  public static async Cancelar(devolucaoId: number): Promise<Response> {
    try {
      const response = await Api.post(`api/Devolucao/Cancelar/${devolucaoId}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return DevolucaoController.erro(error);
    }
  }
}

export default DevolucaoController;
